/**
 * TOP 7 - cart-drawer quantity-bundle upgrades.
 *
 * Clicking a card SETS the cart line to the card's quantity (a target, never an
 * amount to add), through the theme's own cart contract:
 *   xCartHelper.withCartLock -> /cart/change.js -> reRenderSections
 * so the drawer, totals and cart bubble all refresh the same way they do for the
 * theme's own +/- buttons, and the drawer stays open.
 *
 * The listener is delegated on `document`, so it survives the drawer HTML being
 * replaced after every cart update. The cards themselves are rendered by Liquid
 * (snippets/top7-cart-bundles.liquid), which is why no client-side filtering is
 * needed - the re-rendered markup is already correct for the new quantity.
 */
(() => {
  if (window.__top7CartBundles) return;
  window.__top7CartBundles = true;

  let busy = false;

  const root = () => document.querySelector('[data-t7cb]');

  const showError = (message) => {
    const box = root();
    if (!box) return;
    const el = box.querySelector('[data-t7cb-error]');
    if (el) el.textContent = message || 'Something went wrong. Please try again.';
    box.classList.add('t7cb--error');
  };

  const clearError = () => {
    const box = root();
    if (box) box.classList.remove('t7cb--error');
  };

  // ---------- Instant drawer updates (optimistic, pre-priced in Liquid) ----------
  const data = () => {
    const el = document.querySelector('#CartDrawer [data-t7ci]') || document.querySelector('[data-t7ci]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };
  const setText = (el, v) => { if (el && v != null && el.textContent !== v) el.textContent = v; };

  const applyQty = (qty) => {
    const d = data();
    if (!d || !qty || qty < 1) return;
    const st = d.states[Math.min(qty, d.states.length) - 1];
    if (!st) return;
    const drawer = document.getElementById('CartDrawer') || document;
    const input = document.getElementById('cart-qty-' + d.key);
    const row = input && input.closest('.cart-item');
    if (row) row.querySelectorAll('.price-sale').forEach((el) => setText(el, st.u));
    const totals = drawer.querySelectorAll('#total-price');
    totals.forEach((el) => {
      if (el.tagName === 'S') setText(el, st.w);
      else if (el.tagName === 'P' && el.classList.contains('h4')) setText(el, st.t);
    });
    const saveP = drawer.querySelector('.cart-drawer-price-total > div:nth-child(2) > p');
    if (saveP && st.s && saveP.innerHTML !== st.s) saveP.innerHTML = st.s;
    const box = drawer.querySelector('[data-t7cb]');
    if (box) {
      let any = false;
      box.querySelectorAll('[data-t7cb-target]').forEach((c) => {
        const show = parseInt(c.getAttribute('data-t7cb-target'), 10) > qty;
        c.hidden = !show;
        if (show) any = true;
      });
      box.hidden = !any;
    }
  };

  const qtyFromInput = () => {
    const d = data();
    const input = d && document.getElementById('cart-qty-' + d.key);
    return input ? parseInt(input.value, 10) : 0;
  };

  // The theme's +/- handlers update the input synchronously; this document-level
  // listener runs after them, so the new value is already in the field.
  document.addEventListener('click', (event) => {
    if (!event.target.closest('#CartDrawer .cart-item__quantity button, .cart-item__quantity button')) return;
    applyQty(qtyFromInput());
  });
  document.addEventListener('input', (event) => {
    const d = data();
    if (d && event.target.id === 'cart-qty-' + d.key) applyQty(parseInt(event.target.value, 10));
  });

  document.addEventListener('click', (event) => {
    const card = event.target.closest('[data-t7cb-target]');
    if (!card) return;
    event.preventDefault();


    const box = card.closest('[data-t7cb]');
    if (!box || busy) return;

    const quantity = parseInt(card.getAttribute('data-t7cb-target'), 10);
    const lineKey = box.getAttribute('data-t7cb-line');
    if (!quantity || !lineKey) return;

    const helper = window.Alpine && Alpine.store('xCartHelper');
    if (!helper) return; // theme cart layer not ready; do nothing rather than half-update

    busy = true;
    clearError();
    // Optimistic: show the new state immediately, the request below confirms it.
    {
      const d = data();
      const input = d && document.getElementById('cart-qty-' + d.key);
      if (input) { input.value = quantity; applyQty(quantity); }
    }
    card.setAttribute('aria-busy', 'true');
    // Every card is disabled, not just the tapped one: a second tap on a
    // different card mid-flight would queue a conflicting target quantity.
    box.querySelectorAll('[data-t7cb-target]').forEach((el) => { el.disabled = true; });

    const done = () => {
      busy = false;
      // The drawer is usually replaced by now; these are for the failure path,
      // where the original nodes are still on screen.
      card.removeAttribute('aria-busy');
      box.querySelectorAll('[data-t7cb-target]').forEach((el) => { el.disabled = false; });
    };

    const sections = helper.getSectionsToRender ? helper.getSectionsToRender().map((s) => s.id) : undefined;

    helper.withCartLock((standardEvent) => (
      fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity, sections: sections })
      })
        .then((response) => (helper.cartJson ? helper.cartJson(response) : response.json()))
        .then((cart) => {
          if (cart && cart.status && Number(cart.status) >= 400) {
            throw new Error(cart.description || cart.message || '');
          }
          if (cart && cart.sections && helper.reRenderSections) helper.reRenderSections(cart.sections);
          document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
          if (standardEvent && standardEvent.settle) {
            standardEvent.settle(cart, helper.userErrorsFrom ? helper.userErrorsFrom(cart) : []);
          }
          return cart;
        })
        .catch((error) => {
          // Nothing is faked on failure: the cart is left exactly as it was and
          // the shopper can retry.
          if (standardEvent && standardEvent.fail) standardEvent.fail(error);
          showError(error && error.message);
          console.error('TOP7 bundle upgrade failed:', error);
        })
        .finally(done)
    ), {
      type: 'shopify:cart:lines-update',
      action: 'update',
      context: 'cart',
      lines: [{ id: lineKey, quantity: quantity }]
    });
  });
})();
